import { useEffect, useRef, useState } from "react";

interface IntroVideoGateProps {
  onComplete: () => void;
}

// Module-level singleton guard — ensures only ONE IntroVideoGate instance
// ever owns the video element, even under React StrictMode double-mounts or
// rapid tap-induced re-renders. Duplicate mounts immediately yield via
// onComplete so the parent unmounts them without ever attaching a video.
let __introGateMounted = false;
// Reentrancy guard for finish() — protects against rapid double taps that
// could otherwise call onComplete twice before React unmounts the gate.
let __introGateFinishing = false;

/**
 * Plays the brand intro film between the loading bar and the listen page.
 * - Auto-plays muted + inline (mobile-safe).
 * - Auto-advances when the video ends.
 * - "Tap to continue" is always available as a manual escape hatch.
 */
export default function IntroVideoGate({ onComplete }: IntroVideoGateProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [exiting, setExiting] = useState(false);
  const [muted, setMuted] = useState(false);
  const [allowed] = useState(() => {
    if (__introGateMounted) return false;
    __introGateMounted = true;
    return true;
  });

  // Yield immediately if this is a duplicate mount.
  useEffect(() => {
    if (!allowed) {
      onComplete();
      return;
    }
    return () => {
      __introGateMounted = false;
      __introGateFinishing = false;
    };
  }, [allowed, onComplete]);


  // Pick the lighter source on small screens.
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;
  const src = isMobile ? "/love-language-intro-mobile.mp4" : "/love-language-intro-web.mp4";

  useEffect(() => {
    if (!allowed) return;
    const v = videoRef.current;
    if (!v) return;
    v.volume = 1;
    // Try unmuted autoplay first; fall back to muted if browser blocks it.
    v.muted = false;
    v.play()
      .then(() => setMuted(false))
      .catch(() => {
        v.muted = true;
        setMuted(true);
        v.play().catch(() => {
          /* user can tap to continue */
        });
      });
  }, []);

  const finish = () => {
    if (exiting || __introGateFinishing) return;
    __introGateFinishing = true;
    setExiting(true);
    // Hard-stop the video so audio/poster can't bleed over the listen page
    // during the parent's mount/unmount swap.
    const v = videoRef.current;
    if (v) {
      try {
        v.pause();
        v.muted = true;
        v.volume = 0;
        v.currentTime = 0;
        v.removeAttribute("src");
        // iOS Safari keeps decoded audio tracks alive even after pause + src
        // removal, which can cause faint bleed-through into the listen page's
        // playback. Explicitly stop any MediaStream tracks and force a reload
        // to flush the underlying audio session.
        const isIOS =
          typeof navigator !== "undefined" &&
          (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === "MacIntel" && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1));
        if (isIOS) {
          const stream = (v as HTMLVideoElement & { srcObject?: MediaStream | null }).srcObject;
          if (stream && typeof stream.getTracks === "function") {
            stream.getTracks().forEach((t) => t.stop());
            (v as HTMLVideoElement & { srcObject?: MediaStream | null }).srcObject = null;
          }
        }
        v.load();
      } catch {
        /* ignore */
      }
    }
    // Hand off immediately — no overlap window with the listen page.
    onComplete();
  };

  // Duplicate mount — render nothing; the singleton effect already handed off.
  if (!allowed) return null;

  return (
    <div
      className={`fixed inset-0 z-[150] bg-background flex flex-col items-center justify-center transition-opacity duration-300 ${
        exiting ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
      onClick={finish}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") finish();
      }}
      aria-label="Tap to continue to listen page"
    >
      <video
        ref={videoRef}
        src={src}
        poster="/love-language-intro-poster.jpg"
        autoPlay
        playsInline
        preload="auto"
        onEnded={finish}
        className="max-w-full max-h-full w-auto h-auto object-contain"
      />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          const v = videoRef.current;
          if (!v) return;
          const next = !muted;
          v.muted = next;
          if (!next) {
            v.volume = 1;
            v.play().catch(() => {
              v.muted = true;
              setMuted(true);
            });
          }
          setMuted(next);
        }}
        aria-label={muted ? "Unmute intro video" : "Mute intro video"}
        className="absolute top-6 right-6 text-xs tracking-[0.2em] uppercase text-foreground/70 hover:text-foreground transition-colors px-3 py-2 border border-foreground/20 rounded-full backdrop-blur-sm"
        style={{ top: "calc(env(safe-area-inset-top) + 1rem)", right: "calc(env(safe-area-inset-right) + 1rem)" }}
      >
        {muted ? "Unmute" : "Mute"}
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          finish();
        }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-xs tracking-[0.2em] uppercase text-foreground/70 hover:text-foreground transition-colors px-4 py-2"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.5rem)" }}
      >
        Tap to continue →
      </button>
    </div>
  );
}
